import React, { useState, useRef, useEffect } from "react";
import { MediaComment, UpdateMediaCommentDto } from "../../types/media";

interface MediaCommentItemProps {
  comment: MediaComment;
  isOwner: boolean;
  isUpdating?: boolean;
  isDeleting?: boolean;
  onUpdate: (commentId: string, updateData: UpdateMediaCommentDto) => Promise<void>;
  onDeleteClick: (comment: MediaComment) => void;
}

const MediaCommentItem: React.FC<MediaCommentItemProps> = ({
  comment,
  isOwner,
  isUpdating = false,
  isDeleting = false,
  onUpdate,
  onDeleteClick,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [showActions, setShowActions] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current && isEditing) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
      textareaRef.current.focus();
    }
  }, [isEditing, editContent]);

  const handleSaveEdit = async () => {
    if (editContent.trim() && editContent !== comment.content) {
      try {
        await onUpdate(comment.id, { content: editContent.trim() });
        setIsEditing(false);
      } catch (error) {
        console.error("Failed to update comment:", error);
      }
    } else {
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditContent(comment.content);
    setIsEditing(false);
  };

  const handleDelete = () => {
    onDeleteClick(comment);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div data-section="media-comment-item-root"
      className="group relative overflow-hidden bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl hover:bg-white/10 transition-all duration-500 hover:scale-[1.02] hover:shadow-[rgba(var(--primary-blue-rgb),0.2)]"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[rgba(var(--primary-green-rgb),0.03)] to-[rgba(var(--primary-blue-rgb),0.03)]" />
      <div className="absolute -top-10 -right-10 w-20 h-20 bg-[rgba(var(--primary-green-rgb),0.05)] rounded-full blur-xl group-hover:bg-[rgba(var(--primary-green-rgb),0.1)] transition-all duration-500" />
      <div className="absolute -bottom-10 -left-10 w-20 h-20 bg-blue-500/5 rounded-full blur-xl group-hover:bg-blue-500/10 transition-all duration-500" />

      <div className="relative">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 jp-gradient-brand rounded-full flex items-center justify-center text-white font-semibold text-sm">
              {comment.userEmail.charAt(0).toUpperCase()}
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <span className="font-medium text-white">{comment.userEmail}</span>
                <span className="text-gray-400">•</span>
                <span className="font-medium">{formatDate(comment.createdAt)}</span>
                {comment.updatedAt !== comment.createdAt && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-orange-500/20 rounded-full text-orange-300">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span className="text-xs font-medium">edited</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {isOwner && (
            <div className={`flex items-center gap-2 transition-all duration-300 ${showActions || isEditing ? 'opacity-100 transform translate-x-0' : 'opacity-0 transform translate-x-4'}`}>
              {!isEditing && (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    disabled={isUpdating || isDeleting}
                    className="p-2 bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white rounded-xl transition-all duration-300 disabled:opacity-50 group/btn"
                    title="Edit comment"
                  >
                    <svg className="w-4 h-4 group-hover/btn:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>

                  <button
                    onClick={handleDelete}
                    disabled={isUpdating}
                    className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 rounded-xl transition-all duration-300 disabled:opacity-50 group/btn"
                    title="Delete comment"
                  >
                    {isDeleting ? (
                      <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                    ) : (
                      <svg className="w-4 h-4 group-hover/btn:scale-110 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-4">
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[rgba(var(--primary-blue-rgb),0.35)] focus:border-[rgba(var(--primary-blue-rgb),0.55)] transition-all duration-300 resize-none min-h-[80px]"
                placeholder="Edit your comment..."
                disabled={isUpdating}
                maxLength={500}
              />
              <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                {editContent.length}/500
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelEdit}
                disabled={isUpdating}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white rounded-xl transition-all duration-300 border border-white/20 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={!editContent.trim() || isUpdating || editContent.length > 500}
                className="group relative overflow-hidden px-6 py-2 jp-fill-green text-white rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-[rgba(var(--primary-green-rgb),0.35)]"
              >
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative flex items-center gap-2">
                  {isUpdating && (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  )}
                  <span className="font-medium">
                    {isUpdating ? 'Saving...' : 'Save'}
                  </span>
                </div>
              </button>
            </div>
          </div>
        ) : (
          <div className="relative">
            <p className="text-gray-100 text-lg leading-relaxed whitespace-pre-wrap">
              {comment.content}
            </p>
          </div>
        )}

        {(isDeleting || isUpdating) && !isEditing && (
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl px-4 py-2 flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-[rgba(var(--primary-green-rgb),0.3)] border-t-[rgb(var(--primary-green-rgb))] rounded-full animate-spin" />
              <span className="text-sm text-white font-medium">
                {isDeleting ? 'Deleting...' : 'Updating...'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaCommentItem;